import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { 
  FiTag, FiPlus, FiPercent, FiDollarSign, FiCalendar, FiUsers, FiCheck,
  FiX, FiEdit3, FiTrash2, FiPlay, FiPause, FiCopy, FiTrendingUp,
  FiActivity, FiFilter, FiSearch, FiBarChart2, FiGift, FiClock,
  FiTarget, FiZap, FiAward, FiShoppingCart, FiDownload, FiPackage,
  FiBookOpen, FiBook, FiFileText, FiCode, FiLayers
} = FiIcons;

const Promotions = () => {
  const [activeTab, setActiveTab] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPromo, setSelectedPromo] = useState(null);
  const [filterCategory, setFilterCategory] = useState('all');

  const promotions = [
    // Subscription Promotions
    {
      id: 1,
      code: 'NEWYEAR2024',
      name: 'New Year Special',
      description: 'Celebrate 2024 with 30% off all Professional plans',
      type: 'percentage',
      category: 'subscription',
      value: 30,
      status: 'active',
      usage: 234,
      limit: 500,
      revenue: 12450,
      startDate: '2024-01-01',
      endDate: '2024-01-31',
      applicableTo: ['Professional Plan', 'Enterprise Plan'],
      color: 'blue'
    },
    // Course Bundle Promotions
    {
      id: 2,
      code: 'BUNDLE50',
      name: 'Complete Implantology Bundle',
      description: 'Get 3 advanced implant courses + SOPs + Templates for 50% off',
      type: 'percentage',
      category: 'bundle',
      value: 50,
      status: 'active',
      usage: 156,
      limit: 300,
      revenue: 23400,
      startDate: '2024-01-01',
      endDate: '2024-03-31',
      bundleItems: [
        'Advanced Implantology Course',
        'Bone Grafting Masterclass',
        'Complex Cases Workshop',
        'Implant SOPs Pack',
        'Treatment Templates'
      ],
      originalPrice: 1497,
      discountedPrice: 748,
      applicableTo: ['Course Bundle'],
      color: 'purple'
    },
    // Course Promotions
    {
      id: 3,
      code: 'LEARN30',
      name: 'Course Sale - January',
      description: '30% off all individual courses this month',
      type: 'percentage',
      category: 'course',
      value: 30,
      status: 'active',
      usage: 412,
      limit: 1000,
      revenue: 18960,
      startDate: '2024-01-01',
      endDate: '2024-01-31',
      applicableTo: ['All Courses'],
      color: 'green'
    },
    // eBook Promotions
    {
      id: 4,
      code: 'EBOOK20',
      name: 'Digital Library Access',
      description: 'Get 20% off our complete eBook collection',
      type: 'percentage',
      category: 'ebook',
      value: 20,
      status: 'active',
      usage: 289,
      limit: 500,
      revenue: 8670,
      startDate: '2024-01-01',
      endDate: '2024-02-28',
      applicableTo: ['All eBooks', 'eBook Bundles'],
      color: 'orange'
    },
    // Automation Script Promotions
    {
      id: 5,
      code: 'AUTOMATE100',
      name: 'Automation Scripts Pack',
      description: '$100 off premium automation scripts and workflows',
      type: 'fixed',
      category: 'automation',
      value: 100,
      status: 'active',
      usage: 67,
      limit: 200,
      revenue: 6700,
      startDate: '2024-01-15',
      endDate: '2024-02-15',
      applicableTo: ['Automation Scripts', 'Workflow Templates'],
      color: 'indigo'
    },
    // SOP Promotions
    {
      id: 6,
      code: 'SOP40',
      name: 'Practice SOPs Bundle',
      description: '40% off comprehensive Standard Operating Procedures',
      type: 'percentage',
      category: 'sop',
      value: 40,
      status: 'active',
      usage: 123,
      limit: 300,
      revenue: 7380,
      startDate: '2024-01-01',
      endDate: '2024-03-31',
      applicableTo: ['SOP Templates', 'Practice Management SOPs'],
      color: 'teal'
    },
    // Mega Bundle
    {
      id: 7,
      code: 'MEGADEAL',
      name: 'Complete Practice Growth Bundle',
      description: 'Everything you need: Courses + eBooks + SOPs + Automation',
      type: 'percentage',
      category: 'mega-bundle',
      value: 60,
      status: 'active',
      usage: 45,
      limit: 100,
      revenue: 13500,
      startDate: '2024-01-01',
      endDate: '2024-12-31',
      bundleItems: [
        '10 Premium Courses',
        '20 eBooks',
        '50 SOP Templates',
        '30 Automation Scripts',
        'Lifetime Updates'
      ],
      originalPrice: 4997,
      discountedPrice: 1999,
      applicableTo: ['Mega Bundle'],
      color: 'pink'
    }
  ];

  const stats = {
    activePromotions: promotions.filter(p => p.status === 'active').length,
    totalUsage: promotions.reduce((sum, p) => sum + p.usage, 0),
    totalRevenue: promotions.reduce((sum, p) => sum + p.revenue, 0),
    conversionRate: 34.5
  };

  const categories = [
    { id: 'all', label: 'All Promotions', icon: FiTag },
    { id: 'subscription', label: 'Subscriptions', icon: FiAward },
    { id: 'course', label: 'Courses', icon: FiBookOpen },
    { id: 'bundle', label: 'Bundles', icon: FiPackage },
    { id: 'ebook', label: 'eBooks', icon: FiBook },
    { id: 'automation', label: 'Automation', icon: FiCode },
    { id: 'sop', label: 'SOPs', icon: FiFileText },
    { id: 'mega-bundle', label: 'Mega Deals', icon: FiLayers }
  ];

  const getColorClasses = (color) => {
    const colors = {
      blue: 'bg-blue-50 border-blue-200 text-blue-600',
      green: 'bg-green-50 border-green-200 text-green-600',
      purple: 'bg-purple-50 border-purple-200 text-purple-600',
      orange: 'bg-orange-50 border-orange-200 text-orange-600',
      indigo: 'bg-indigo-50 border-indigo-200 text-indigo-600',
      teal: 'bg-teal-50 border-teal-200 text-teal-600',
      pink: 'bg-pink-50 border-pink-200 text-pink-600'
    };
    return colors[color] || colors.blue;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'scheduled': return 'bg-blue-100 text-blue-800';
      case 'expired': return 'bg-gray-100 text-gray-800';
      case 'paused': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'subscription': return FiAward;
      case 'course': return FiBookOpen;
      case 'bundle': return FiPackage;
      case 'ebook': return FiBook;
      case 'automation': return FiCode;
      case 'sop': return FiFileText;
      case 'mega-bundle': return FiLayers;
      default: return FiTag;
    }
  };

  const filteredPromotions = promotions.filter(promo => {
    const matchesSearch = promo.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         promo.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         promo.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesTab = activeTab === 'all' || promo.status === activeTab;
    const matchesCategory = filterCategory === 'all' || promo.category === filterCategory;
    return matchesSearch && matchesTab && matchesCategory;
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-orange-50 to-yellow-50">
      <div className="max-w-7xl mx-auto p-6">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Promotions & Bundles</h1>
              <p className="text-gray-600 mt-1">Manage discounts for courses, bundles, eBooks, SOPs, and automation</p>
            </div>
            <div className="mt-4 sm:mt-0 flex items-center space-x-3">
              <button className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center space-x-2">
                <SafeIcon icon={FiDownload} className="w-4 h-4" />
                <span>Export</span>
              </button>
              <button 
                onClick={() => setShowCreateModal(true)}
                className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors flex items-center space-x-2"
              >
                <SafeIcon icon={FiPlus} className="w-4 h-4" />
                <span>Create Promotion</span>
              </button>
            </div>
          </div>
        </motion.div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-xl p-6 shadow-sm border border-gray-100"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-orange-50 rounded-lg">
                <SafeIcon icon={FiTag} className="w-6 h-6 text-orange-600" />
              </div>
              <span className="text-sm text-green-600 font-medium">+7</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">{stats.activePromotions}</p>
            <p className="text-sm text-gray-600 mt-1">Active Promotions</p>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-xl p-6 shadow-sm border border-gray-100"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-blue-50 rounded-lg">
                <SafeIcon icon={FiUsers} className="w-6 h-6 text-blue-600" />
              </div>
              <span className="text-sm text-green-600 font-medium">+24%</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">{stats.totalUsage}</p>
            <p className="text-sm text-gray-600 mt-1">Total Redemptions</p>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-xl p-6 shadow-sm border border-gray-100"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-green-50 rounded-lg">
                <SafeIcon icon={FiDollarSign} className="w-6 h-6 text-green-600" />
              </div>
              <span className="text-sm text-green-600 font-medium">+35%</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">${stats.totalRevenue.toLocaleString()}</p>
            <p className="text-sm text-gray-600 mt-1">Revenue Generated</p>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-white rounded-xl p-6 shadow-sm border border-gray-100"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-purple-50 rounded-lg">
                <SafeIcon icon={FiTrendingUp} className="w-6 h-6 text-purple-600" />
              </div>
              <span className="text-sm text-green-600 font-medium">+5.2%</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">{stats.conversionRate}%</p>
            <p className="text-sm text-gray-600 mt-1">Conversion Rate</p>
          </motion.div>
        </div>

        {/* Category Filters */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 mb-6"
        >
          <div className="flex flex-wrap gap-3">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setFilterCategory(category.id)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center space-x-2 ${
                  filterCategory === category.id
                    ? 'bg-orange-50 text-orange-600 border-2 border-orange-200'
                    : 'text-gray-600 hover:bg-gray-50 border-2 border-transparent'
                }`}
              >
                <SafeIcon icon={category.icon} className="w-4 h-4" />
                <span>{category.label}</span>
                <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full text-xs">
                  {promotions.filter(p => category.id === 'all' || p.category === category.id).length}
                </span>
              </button>
            ))}
          </div>
        </motion.div>

        {/* Search and Status Filters */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 mb-6"
        >
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
            <div className="flex items-center space-x-4">
              <div className="relative">
                <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search promotions..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent w-64"
                />
              </div>
            </div>
            <div className="flex items-center space-x-2">
              {['all', 'active', 'scheduled', 'expired'].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    activeTab === tab
                      ? 'bg-orange-50 text-orange-600 border border-orange-200'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {tab.charAt(0).toUpperCase() + tab.slice(1)}
                </button>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Promotions Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {filteredPromotions.map((promo, index) => (
            <motion.div
              key={promo.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 + index * 0.1 }}
              className={`bg-white rounded-xl shadow-sm border-2 overflow-hidden hover:shadow-md transition-all ${getColorClasses(promo.color)}`}
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className={`p-3 rounded-lg ${getColorClasses(promo.color)}`}>
                      <SafeIcon icon={getCategoryIcon(promo.category)} className="w-6 h-6" />
                    </div>
                    <div>
                      <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-700 capitalize">
                        {promo.category.replace('-', ' ')}
                      </span>
                    </div>
                  </div>
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(promo.status)}`}>
                    {promo.status}
                  </span>
                </div>

                <h3 className="text-lg font-bold text-gray-900 mb-2">{promo.name}</h3>
                <p className="text-sm text-gray-600 mb-4">{promo.description}</p>

                {/* Promo Code */}
                <div className="flex items-center space-x-2 mb-4 p-3 bg-gray-50 rounded-lg">
                  <code className="text-lg font-bold text-gray-900">{promo.code}</code>
                  <button className="ml-auto p-1 text-gray-400 hover:text-gray-600">
                    <SafeIcon icon={FiCopy} className="w-4 h-4" />
                  </button>
                </div>

                {/* Bundle Items (if applicable) */}
                {promo.bundleItems && (
                  <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                    <p className="text-xs font-semibold text-gray-700 mb-2">Bundle Includes:</p>
                    <ul className="space-y-1">
                      {promo.bundleItems.slice(0, 3).map((item, i) => (
                        <li key={i} className="flex items-center space-x-2 text-xs text-gray-600">
                          <SafeIcon icon={FiCheck} className="w-3 h-3 text-green-500" />
                          <span>{item}</span>
                        </li>
                      ))}
                      {promo.bundleItems.length > 3 && (
                        <li className="text-xs text-gray-500 ml-5">
                          +{promo.bundleItems.length - 3} more items
                        </li>
                      )}
                    </ul>
                  </div>
                )}

                {/* Pricing (for bundles) */}
                {promo.originalPrice && (
                  <div className="mb-4 flex items-center justify-between p-3 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg">
                    <div>
                      <p className="text-xs text-gray-500 line-through">${promo.originalPrice}</p>
                      <p className="text-2xl font-bold text-green-600">${promo.discountedPrice}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-gray-500">You Save</p>
                      <p className="text-lg font-bold text-green-600">
                        ${promo.originalPrice - promo.discountedPrice}
                      </p>
                    </div>
                  </div>
                )}

                {/* Stats Grid */}
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div className="flex items-center space-x-2 text-sm text-gray-600">
                    <SafeIcon icon={FiGift} className="w-4 h-4" />
                    <span>
                      {promo.type === 'percentage' ? `${promo.value}% off` : `$${promo.value} off`}
                    </span>
                  </div>
                  <div className="flex items-center space-x-2 text-sm text-gray-600">
                    <SafeIcon icon={FiUsers} className="w-4 h-4" />
                    <span>{promo.usage} / {promo.limit} used</span>
                  </div>
                  <div className="flex items-center space-x-2 text-sm text-gray-600">
                    <SafeIcon icon={FiCalendar} className="w-4 h-4" />
                    <span>Until {new Date(promo.endDate).toLocaleDateString()}</span>
                  </div>
                  <div className="flex items-center space-x-2 text-sm text-green-600 font-semibold">
                    <SafeIcon icon={FiDollarSign} className="w-4 h-4" />
                    <span>${promo.revenue.toLocaleString()}</span>
                  </div>
                </div>

                {/* Progress Bar */}
                <div className="mb-4">
                  <div className="flex items-center justify-between text-sm mb-2">
                    <span className="text-gray-600">Usage</span>
                    <span className="font-semibold text-gray-900">
                      {Math.round((promo.usage / promo.limit) * 100)}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className={`h-2 rounded-full ${
                        promo.color === 'blue' ? 'bg-blue-500' :
                        promo.color === 'green' ? 'bg-green-500' :
                        promo.color === 'purple' ? 'bg-purple-500' :
                        promo.color === 'orange' ? 'bg-orange-500' :
                        promo.color === 'indigo' ? 'bg-indigo-500' :
                        promo.color === 'teal' ? 'bg-teal-500' :
                        'bg-pink-500'
                      }`}
                      style={{ width: `${(promo.usage / promo.limit) * 100}%` }}
                    />
                  </div>
                </div>

                {/* Applicable To */}
                <div className="mb-4">
                  <p className="text-xs text-gray-500 mb-2">Applicable to:</p>
                  <div className="flex flex-wrap gap-2">
                    {promo.applicableTo.map((item, i) => (
                      <span key={i} className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full">
                        {item}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center space-x-2 pt-4 border-t border-gray-200">
                  <button 
                    onClick={() => {
                      setSelectedPromo(promo);
                      setShowCreateModal(true);
                    }}
                    className="flex-1 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors flex items-center justify-center space-x-2"
                  >
                    <SafeIcon icon={FiEdit3} className="w-4 h-4" />
                    <span>Edit</span>
                  </button>
                  <button className="p-2 text-gray-400 hover:text-gray-600 transition-colors">
                    {promo.status === 'active' ? (
                      <SafeIcon icon={FiPause} className="w-5 h-5" />
                    ) : (
                      <SafeIcon icon={FiPlay} className="w-5 h-5" />
                    )}
                  </button>
                  <button className="p-2 text-gray-400 hover:text-blue-600 transition-colors">
                    <SafeIcon icon={FiBarChart2} className="w-5 h-5" />
                  </button>
                  <button className="p-2 text-gray-400 hover:text-red-600 transition-colors">
                    <SafeIcon icon={FiTrash2} className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Create/Edit Modal */}
        <AnimatePresence>
          {showCreateModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
              onClick={() => {
                setShowCreateModal(false);
                setSelectedPromo(null);
              }}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto"
                onClick={e => e.stopPropagation()}
              >
                <div className="p-6 border-b border-gray-200 sticky top-0 bg-white z-10">
                  <div className="flex justify-between items-center">
                    <div>
                      <h3 className="text-xl font-bold text-gray-800">
                        {selectedPromo ? 'Edit Promotion' : 'Create New Promotion'}
                      </h3>
                      <p className="text-sm text-gray-500 mt-1">Set up promotional offers for any product type</p>
                    </div>
                    <button
                      onClick={() => {
                        setShowCreateModal(false);
                        setSelectedPromo(null);
                      }}
                      className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      <SafeIcon icon={FiX} className="w-6 h-6" />
                    </button>
                  </div>
                </div>

                <div className="p-6 space-y-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Category *
                    </label>
                    <select 
                      defaultValue={selectedPromo?.category}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    >
                      <option value="subscription">Subscription Plans</option>
                      <option value="course">Individual Courses</option>
                      <option value="bundle">Course Bundles</option>
                      <option value="ebook">eBooks</option>
                      <option value="automation">Automation Scripts</option>
                      <option value="sop">SOPs & Templates</option>
                      <option value="mega-bundle">Mega Bundles</option>
                    </select>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Promotion Name *
                      </label>
                      <input
                        type="text"
                        defaultValue={selectedPromo?.name}
                        placeholder="e.g., New Year Special"
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Promo Code *
                      </label>
                      <input
                        type="text"
                        defaultValue={selectedPromo?.code}
                        placeholder="e.g., NEWYEAR2024"
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent uppercase"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Description
                    </label>
                    <textarea
                      defaultValue={selectedPromo?.description}
                      placeholder="Describe the promotion..."
                      rows={3}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Discount Type *
                      </label>
                      <select 
                        defaultValue={selectedPromo?.type}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      >
                        <option value="percentage">Percentage Off</option>
                        <option value="fixed">Fixed Amount Off</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Discount Value *
                      </label>
                      <input
                        type="number"
                        defaultValue={selectedPromo?.value}
                        placeholder="30"
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Start Date *
                      </label>
                      <input
                        type="date"
                        defaultValue={selectedPromo?.startDate}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        End Date *
                      </label>
                      <input
                        type="date"
                        defaultValue={selectedPromo?.endDate}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Usage Limit
                    </label>
                    <input
                      type="number"
                      defaultValue={selectedPromo?.limit}
                      placeholder="500"
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Applicable To (Select products/plans)
                    </label>
                    <div className="border border-gray-300 rounded-lg p-4 max-h-40 overflow-y-auto">
                      <div className="space-y-2">
                        {['All Products', 'Professional Plan', 'Enterprise Plan', 'Implantology Course', 'Digital Workflow Course'].map((item) => (
                          <label key={item} className="flex items-center space-x-2">
                            <input
                              type="checkbox"
                              className="rounded text-orange-600 focus:ring-orange-500"
                            />
                            <span className="text-sm text-gray-700">{item}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="p-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-3 sticky bottom-0">
                  <button
                    onClick={() => {
                      setShowCreateModal(false);
                      setSelectedPromo(null);
                    }}
                    className="py-2.5 px-5 bg-white border border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button className="py-2.5 px-6 bg-orange-600 text-white font-semibold rounded-lg hover:bg-orange-700 transition-colors">
                    {selectedPromo ? 'Save Changes' : 'Create Promotion'}
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default Promotions;